import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { AppTextService } from '@fi-shared/app-text';
import { ChecklistModel } from '@fi-app/checklist/checklist.model';
import { IChecklistReportData, IChecklistReportSelectionData } from '../../checklist-report';
import { ChecklistReportHelperService } from '../../checklist-report-helper.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { ChartEvent, ChartOptions } from 'chart.js';
import { IChartClickEvent } from '@fi-shared/fi-chart/fi-chart';

@Component({
    selector: 'fi-checklist-report-view-pie-chart',
    templateUrl: './checklist-report-view-pie-chart.component.html',
    styleUrls: ['./checklist-report-view-pie-chart.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checklist-report-view-pie-chart' },
    standalone: false,
})
export class ChecklistReportViewPieChartComponent implements OnInit, OnChanges {
    @Input() reportData: IChecklistReportData;
    @Input() selectionData: IChecklistReportSelectionData;
    @Input() checklistId: number;

    chartData: number[];
    chartColors: string[];
    chartLabels: string[];
    chartOptions: ChartOptions<'doughnut'>;
    checkpointsChartOptions: ChartOptions<'doughnut'>;

    checkpointsChartData: number[];
    checkpointsChartLabels: any[];
    checkpointsChartColors: string[];

    completedNumberOfUnits: number;
    completedPercentage: any;
    deviationsCheckpoints: number;
    totalCheckpoints: number;
    totalNumberOfUnits: number;

    tableRows: {
        class: string;
        value: number;
        title: string;
        status: string | number;
        titleMobile?: string;
    }[];

    constructor(
        private appTextService: AppTextService,
        private checklistReportHelperService: ChecklistReportHelperService,
        private stateService: FiStateService,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        const change = changes.reportData;
        if (change && !change.firstChange) {
            this.updateChartData();
        }
    }

    ngOnInit() {
        this.updateChartData();
        this.chartOptions = {
            cutout: '75%',
            animation: {
                duration: 1500,
                easing: 'easeOutBounce',
            },
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                tooltip: {
                    enabled: true,
                    callbacks: {
                        label: (label) => {
                            const value = label.dataset.data[label.dataIndex];
                            return ((value / this.totalNumberOfUnits) * 100).toFixed(0) + '%';
                        },
                    },
                },
                legend: {
                    display: false,
                },
            },
        };

        this.checkpointsChartOptions = {
            cutout: '75%',
            animation: {
                duration: 1500,
                easing: 'easeOutBounce',
            },
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                tooltip: {
                    enabled: true,
                    callbacks: {
                        label: (label) => {
                            if (this.totalCheckpoints === 0) {
                                return '100%';
                            }
                            const value = label.dataset.data[label.dataIndex];
                            return ((value / this.totalCheckpoints) * 100).toFixed(2) + '%';
                        },
                    },
                },
                legend: {
                    display: false,
                },
            },
        };
    }

    chartClick($event: IChartClickEvent) {
        this.checklistReportHelperService.onChartClick($event, (index) => {
            this.selectStatus(this.reportData.Statistics.Labels[index]);
        });
    }

    checkpointsChartClick($event) {
        this.checklistReportHelperService.onChartClick($event, (index) => {
            this.selectStatus(this.checkpointsChartLabels[index]);
        });
    }

    selectStatus(status, $event?) {
        if ($event) {
            $event.preventDefault();
        }
        this.checklistReportHelperService.selectStatus(status, this.reportData, this.checklistId);
    }

    getTableRowStatusClass(status) {
        if (status && this.stateService.params.status === status.toString()) {
            return 'fi-checklist-status-bg-' + status + '-light';
        }
    }

    isTableRowClickable(data) {
        return !!data.status;
    }

    private updateChartData() {
        this.totalNumberOfUnits = this.reportData.Statistics.Data.reduce((p1, p2) => {
            return p1 + p2;
        }, 0);
        const statusData = {};
        this.chartLabels = [];
        const colors = [];
        this.tableRows = [];

        this.reportData.Statistics.Labels.forEach((label, index) => {
            this.chartLabels.push(this.appTextService.get(ChecklistModel.StatusTextIds[label]));
            colors.push(ChecklistModel.StatusColors[label]);
            statusData[label] = this.reportData.Statistics.Data[index];

            this.tableRows.push({
                class: 'status-' + label,
                value: statusData[label],
                title: this.appTextService.get(ChecklistModel.StatusFullTextIds[label]),
                titleMobile: ChecklistModel.StatusMobileTextIds[label]
                    ? this.appTextService.get(ChecklistModel.StatusMobileTextIds[label])
                    : null,
                status: label,
            });
        });

        this.chartColors = colors;

        const CheckpointsData = {};
        this.reportData.CheckPointStatistics.Labels.forEach((value, index) => {
            CheckpointsData[value] = this.reportData.CheckPointStatistics.Data[index];
        });

        this.checkpointsChartData = [];
        this.checkpointsChartLabels = [];
        const checkpointsChartColors = [];

        const checkpointsDeviationColor = '#FFC107';

        if (CheckpointsData[ChecklistModel.Status.Deviation]) {
            this.tableRows.push({
                class: 'report-status-bg-color-checkpoint-deviations',
                value: CheckpointsData[ChecklistModel.Status.Deviation],
                title: this.appTextService.get('checklist.checkpoint_deviations2'),
                status: ChecklistModel.Status.DeviationCheckpoints,
            });

            this.checkpointsChartData.push(CheckpointsData[ChecklistModel.Status.Deviation]);
            this.checkpointsChartLabels.push(ChecklistModel.Status.DeviationCheckpoints);
            checkpointsChartColors.push(ChecklistModel.StatusColors[ChecklistModel.Status.DeviationCheckpoints]);
        }

        if (CheckpointsData[ChecklistModel.Status.Ok]) {
            this.tableRows.push({
                class: 'report-status-bg-color-ok-checkpoint',
                value: CheckpointsData[ChecklistModel.Status.Ok],
                title: this.appTextService.get('checklist.ok_checkpoints'),
                status: ChecklistModel.Status.OkCheckpoints,
            });

            this.checkpointsChartData.push(CheckpointsData[ChecklistModel.Status.Ok]);
            this.checkpointsChartLabels.push(ChecklistModel.Status.OkCheckpoints);
            checkpointsChartColors.push(ChecklistModel.StatusColors[ChecklistModel.Status.OkCheckpoints]);
        }

        this.completedNumberOfUnits =
            (statusData[ChecklistModel.Status.Ok] || 0) + (statusData[ChecklistModel.Status.Deviation] || 0);

        this.completedPercentage = (this.completedNumberOfUnits / this.totalNumberOfUnits) * 100;
        this.completedPercentage = this.completedPercentage.toFixed(0);

        this.deviationsCheckpoints = CheckpointsData[ChecklistModel.Status.Deviation] || 0;

        this.totalCheckpoints = 0;
        this.checkpointsChartData.forEach((p) => {
            this.totalCheckpoints += p;
        });

        // in case of 0 deviations and 0 completed checkpoints we need to show chart filled with "negative" color
        if (this.totalCheckpoints === 0) {
            this.checkpointsChartData.push(1);
            this.checkpointsChartLabels.push(0);
            checkpointsChartColors.push(checkpointsDeviationColor);
        }

        this.checkpointsChartColors = checkpointsChartColors;
    }

    swipedLeft = false;

    swipeLeft() {
        this.swipedLeft = true;
    }

    swipeRight() {
        this.swipedLeft = false;
    }
}
